import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Plus,
  Play,
  Edit2,
  Trash2,
  Clock,
  Zap,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  RefreshCw,
  ChevronRight,
  Brain,
  FileText,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { autoStoriesAPI, referenceAPI } from '../lib/api';
import { formatDate, formatDateTime, formatNumber, getStoryTypeLabel, cn } from '../lib/utils';
import { toast } from 'sonner';

const STORY_TYPES = [
  { value: 'sector_summary', label: 'Sector Summary' },
  { value: 'company_announcement', label: 'Company Announcement' },
  { value: 'regulatory_action', label: 'Regulatory Action' },
  { value: 'procurement_award', label: 'Procurement Award' },
  { value: 'insolvency_notice', label: 'Insolvency Notice' },
  { value: 'director_appointment', label: 'Director Appointment' },
  { value: 'planning_decision', label: 'Planning Decision' },
  { value: 'government_policy', label: 'Government Policy' },
  { value: 'research_breakdown', label: 'Research Breakdown' },
  { value: 'industry_digest', label: 'Industry Digest' },
];

function StatusBadge({ status }) {
  const configs = {
    pending: { color: 'bg-amber-900/20 text-amber-400 border-amber-900/50', icon: Clock },
    generating: { color: 'bg-blue-900/20 text-blue-400 border-blue-900/50 animate-pulse', icon: Zap },
    review: { color: 'bg-amber-900/20 text-amber-400 border-amber-900/50', icon: Eye },
    approved: { color: 'bg-green-900/20 text-green-400 border-green-900/50', icon: CheckCircle },
    published: { color: 'bg-teal-900/20 text-teal-400 border-teal-900/50', icon: CheckCircle },
    failed: { color: 'bg-red-900/20 text-red-400 border-red-900/50', icon: XCircle },
    skipped: { color: 'bg-slate-800 text-slate-400 border-slate-700', icon: AlertCircle },
  };
  
  const config = configs[status] || configs.pending;
  const Icon = config.icon;

  return (
    <Badge className={`${config.color} border capitalize`}>
      <Icon className="w-3 h-3 mr-1" />
      {status}
    </Badge>
  );
}

export function AutomatedStories() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('templates');
  
  // Templates State
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Runs State
  const [runs, setRuns] = useState([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [runsLoading, setRunsLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [runDetailLoading, setRunDetailLoading] = useState(false);
  
  // Reference Data
  const [publications, setPublications] = useState([]);
  const [clients, setClients] = useState([]);
  const [bylines, setBylines] = useState([]);
  
  // Template Form
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    story_type: '',
    is_active: true,
    publication_id: '',
    client_id: '',
    byline_id: '',
    schedule_enabled: false,
    schedule_cron: '',
    grok_system_prompt: `You are a senior UK business journalist and intelligence analyst generating structured content for a B2B media network.

You must return ONLY a valid JSON object — no preamble, no explanation, no markdown code fences.

The JSON must contain: headline, lede, body, estimated_read_time, source_summary, and di_metadata.`,
    grok_user_prompt_template: '',
    generates_di_records: [],
    default_estimated_read_time: 3,
  });

  // Run Variables Modal
  const [showRunModal, setShowRunModal] = useState(false);
  const [runningTemplate, setRunningTemplate] = useState(null);
  const [runVariables, setRunVariables] = useState({});
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    loadReferenceData();
    loadTemplates();
    loadRuns();
  }, []);

  const loadReferenceData = async () => {
    try {
      const [pubsRes, clientsRes, bylinesRes] = await Promise.all([
        referenceAPI.getPublications(),
        referenceAPI.getClients(),
        referenceAPI.getBylines(),
      ]);
      setPublications(pubsRes.data);
      setClients(clientsRes.data);
      setBylines(bylinesRes.data);
    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const response = await autoStoriesAPI.getTemplates();
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const loadRuns = async () => {
    try {
      setRunsLoading(true);
      const response = await autoStoriesAPI.getRuns({ page_size: 50 });
      setRuns(response.data.runs || []);
      setRunsTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load runs:', error);
    } finally {
      setRunsLoading(false);
    }
  };

  const loadRunDetail = async (runId) => {
    try {
      setRunDetailLoading(true);
      const response = await autoStoriesAPI.getRun(runId);
      setRunDetail(response.data);
    } catch (error) {
      console.error('Failed to load run detail:', error);
      toast.error('Failed to load run details');
    } finally {
      setRunDetailLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      if (editingTemplate) {
        await autoStoriesAPI.updateTemplate(editingTemplate.id, templateForm);
        toast.success('Template updated');
      } else {
        await autoStoriesAPI.createTemplate(templateForm);
        toast.success('Template created');
      }
      setShowTemplateModal(false);
      setEditingTemplate(null);
      resetTemplateForm();
      loadTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await autoStoriesAPI.deleteTemplate(id);
      toast.success('Template deleted');
      loadTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleRunTemplate = async () => {
    if (!runningTemplate) return;
    
    try {
      setIsRunning(true);
      await autoStoriesAPI.runTemplate(runningTemplate.id, runVariables);
      toast.success('Run started');
      setShowRunModal(false);
      setRunningTemplate(null);
      setRunVariables({});
      loadRuns();
    } catch (error) {
      toast.error('Failed to run template');
    } finally {
      setIsRunning(false);
    }
  };

  const handleApproveRun = async (runId) => {
    try {
      await autoStoriesAPI.approveRun(runId);
      toast.success('Run approved and submitted to editorial');
      loadRuns();
      if (runDetail?.id === runId) {
        loadRunDetail(runId);
      }
    } catch (error) {
      toast.error('Failed to approve run');
    }
  };

  const handleRejectRun = async (runId) => {
    const reason = window.prompt('Rejection reason:');
    if (reason === null) return;
    
    try {
      await autoStoriesAPI.rejectRun(runId, reason);
      toast.success('Run rejected');
      loadRuns();
    } catch (error) {
      toast.error('Failed to reject run');
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      description: '',
      story_type: '',
      is_active: true,
      publication_id: '',
      client_id: '',
      byline_id: '',
      schedule_enabled: false,
      schedule_cron: '',
      grok_system_prompt: `You are a senior UK business journalist and intelligence analyst generating structured content for a B2B media network.

You must return ONLY a valid JSON object — no preamble, no explanation, no markdown code fences.

The JSON must contain: headline, lede, body, estimated_read_time, source_summary, and di_metadata.`,
      grok_user_prompt_template: '',
      generates_di_records: [],
      default_estimated_read_time: 3,
    });
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name || '',
      description: template.description || '',
      story_type: template.story_type || '',
      is_active: template.is_active !== false,
      publication_id: template.publication_id || '',
      client_id: template.client_id || '',
      byline_id: template.byline_id || '',
      schedule_enabled: template.schedule_enabled || false,
      schedule_cron: template.schedule_cron || '',
      grok_system_prompt: template.grok_system_prompt || '',
      grok_user_prompt_template: template.grok_user_prompt_template || '',
      generates_di_records: template.generates_di_records || [],
      default_estimated_read_time: template.default_estimated_read_time || 3,
    });
    setShowTemplateModal(true);
  };

  const openRunModal = (template) => {
    setRunningTemplate(template);
    setRunVariables({});
    setShowRunModal(true);
  };

  const handleRunClick = (run) => {
    setSelectedRun(run);
    loadRunDetail(run.id);
  };
  if (!user || user.role !== 'admin') return <Navigate to="/" replace />;
  return (
    <div className="space-y-6" data-testid="automated-stories-page">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-text-primary uppercase tracking-tight">
          Automated Stories
        </h1>
        <p className="text-text-secondary mt-1">Create and manage automated content generation templates</p>
      </div>

      {/* Health Panel */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-surface-card border-border-default">
          <CardContent className="p-4">
            <p className="text-xs text-text-tertiary uppercase">Templates Active</p>
            <p className="text-2xl font-heading font-bold text-text-primary">
              {templates.filter(t => t.is_active).length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface-card border-border-default">
          <CardContent className="p-4">
            <p className="text-xs text-text-tertiary uppercase">Runs Today</p>
            <p className="text-2xl font-heading font-bold text-text-primary">
              {runs.filter(r => {
                const today = new Date().toDateString();
                return new Date(r.created_at).toDateString() === today;
              }).length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface-card border-border-default">
          <CardContent className="p-4">
            <p className="text-xs text-text-tertiary uppercase">Success Rate</p>
            <p className="text-2xl font-heading font-bold text-green-400">
              {runs.length > 0 
                ? ((runs.filter(r => ['review', 'approved', 'published'].includes(r.status)).length / runs.length) * 100).toFixed(0)
                : 0}%
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface-card border-border-default">
          <CardContent className="p-4">
            <p className="text-xs text-text-tertiary uppercase">DI Records Created</p>
            <p className="text-2xl font-heading font-bold text-accent-primary">
              {formatNumber(runs.reduce((sum, r) => sum + (r.di_records_created || 0), 0))}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-bg-tertiary">
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="runs">Run History</TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6 mt-6">
          <div className="flex justify-end">
            <Button onClick={() => setShowTemplateModal(true)} data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </div>

          <Card className="bg-surface-card border-border-default">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="table-header hover:bg-transparent">
                    <TableHead>Template Name</TableHead>
                    <TableHead>Story Type</TableHead>
                    <TableHead>Publication</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Last Run</TableHead>
                    <TableHead>Stories</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templatesLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <div className="animate-spin w-6 h-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : templates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-text-tertiary">
                        No templates created yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    templates.map((template) => (
                      <TableRow key={template.id} className="table-row">
                        <TableCell className="table-cell font-medium">{template.name}</TableCell>
                        <TableCell className="table-cell">
                          <Badge variant="outline">{getStoryTypeLabel(template.story_type)}</Badge>
                        </TableCell>
                        <TableCell className="table-cell">{template.publication_name || '-'}</TableCell>
                        <TableCell className="table-cell">
                          {template.schedule_enabled ? (
                            <Badge className="bg-green-900/20 text-green-400 border-green-900/50">Active</Badge>
                          ) : (
                            <Badge variant="outline">Manual</Badge>
                          )}
                        </TableCell>
                        <TableCell className="table-cell">{formatDate(template.last_run)}</TableCell>
                        <TableCell className="table-cell">{formatNumber(template.stories_generated)}</TableCell>
                        <TableCell className="table-cell">
                          <Badge className={template.is_active ? 'badge-success' : 'badge-neutral'}>
                            {template.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="table-cell">
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openRunModal(template)}
                              data-testid={`run-template-${template.id}`}
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Run
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openEditTemplate(template)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteTemplate(template.id)}>
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Runs Tab */}
        <TabsContent value="runs" className="space-y-6 mt-6">
          <Card className="bg-surface-card border-border-default">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="table-header hover:bg-transparent">
                    <TableHead>Template</TableHead>
                    <TableHead>Story Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Headline</TableHead>
                    <TableHead>DI Records</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runsLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <div className="animate-spin w-6 h-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : runs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-text-tertiary">
                        No runs yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    runs.map((run) => (
                      <TableRow 
                        key={run.id} 
                        className="table-row cursor-pointer"
                        onClick={() => handleRunClick(run)}
                      >
                        <TableCell className="table-cell font-medium">{run.template_name || '-'}</TableCell>
                        <TableCell className="table-cell">
                          <Badge variant="outline">{getStoryTypeLabel(run.story_type)}</Badge>
                        </TableCell>
                        <TableCell className="table-cell">
                          <StatusBadge status={run.status} />
                        </TableCell>
                        <TableCell className="table-cell capitalize">{run.trigger_type}</TableCell>
                        <TableCell className="table-cell max-w-[200px] truncate">
                          {run.headline || '-'}
                        </TableCell>
                        <TableCell className="table-cell">
                          <Badge variant="outline" className="bg-accent-primary/10 text-accent-primary">
                            <Brain className="w-3 h-3 mr-1" />
                            {run.di_records_created || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="table-cell">{formatDateTime(run.started_at)}</TableCell>
                        <TableCell className="table-cell">
                          <ChevronRight className="w-4 h-4 text-text-tertiary" />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Template Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="sm:max-w-2xl bg-surface-card border-border-default max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-text-primary">
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Template Name</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
              <div>
                <Label>Story Type</Label>
                <Select value={templateForm.story_type} onValueChange={(v) => setTemplateForm({...templateForm, story_type: v})}>
                  <SelectTrigger className="bg-bg-tertiary border-border-default">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {STORY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={templateForm.description}
                onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                className="bg-bg-tertiary border-border-default"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Publication</Label>
                <Select value={templateForm.publication_id} onValueChange={(v) => setTemplateForm({...templateForm, publication_id: v})}>
                  <SelectTrigger className="bg-bg-tertiary border-border-default">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {publications.map((pub) => (
                      <SelectItem key={pub.id} value={pub.id}>{pub.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Client (Optional)</Label>
                <Select value={templateForm.client_id} onValueChange={(v) => setTemplateForm({...templateForm, client_id: v})}>
                  <SelectTrigger className="bg-bg-tertiary border-border-default">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Byline</Label>
                <Select value={templateForm.byline_id} onValueChange={(v) => setTemplateForm({...templateForm, byline_id: v})}>
                  <SelectTrigger className="bg-bg-tertiary border-border-default">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {bylines.map((byline) => (
                      <SelectItem key={byline.id} value={byline.id}>{byline.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Grok System Prompt</Label>
              <Textarea
                value={templateForm.grok_system_prompt}
                onChange={(e) => setTemplateForm({...templateForm, grok_system_prompt: e.target.value})}
                className="bg-bg-tertiary border-border-default min-h-[100px] font-mono text-sm"
              />
            </div>

            <div>
              <Label>Grok User Prompt Template</Label>
              <Textarea
                value={templateForm.grok_user_prompt_template}
                onChange={(e) => setTemplateForm({...templateForm, grok_user_prompt_template: e.target.value})}
                placeholder="Use {variable} for dynamic values..."
                className="bg-bg-tertiary border-border-default min-h-[100px] font-mono text-sm"
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={templateForm.is_active}
                  onCheckedChange={(checked) => setTemplateForm({...templateForm, is_active: checked})}
                />
                <Label>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={templateForm.schedule_enabled}
                  onCheckedChange={(checked) => setTemplateForm({...templateForm, schedule_enabled: checked})}
                />
                <Label>Enable Schedule</Label>
              </div>
            </div>

            {templateForm.schedule_enabled && (
              <div>
                <Label>Cron Expression</Label>
                <Input
                  value={templateForm.schedule_cron}
                  onChange={(e) => setTemplateForm({...templateForm, schedule_cron: e.target.value})}
                  placeholder="0 8 * * 1 (Mon 8am)"
                  className="bg-bg-tertiary border-border-default font-mono"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateModal(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} data-testid="save-template-btn">
              {editingTemplate ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run Now Modal */}
      <Dialog open={showRunModal} onOpenChange={setShowRunModal}>
        <DialogContent className="sm:max-w-md bg-surface-card border-border-default">
          <DialogHeader>
            <DialogTitle className="text-text-primary">Run Template</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-text-secondary mb-4">
              Running: <span className="font-medium text-text-primary">{runningTemplate?.name}</span>
            </p>
            <p className="text-sm text-text-tertiary">
              Click Run to start generating a story with Grok AI.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRunModal(false)}>Cancel</Button>
            <Button onClick={handleRunTemplate} disabled={isRunning} data-testid="confirm-run-btn">
              {isRunning ? 'Running...' : 'Run'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run Detail Sheet */}
      <Sheet open={!!selectedRun} onOpenChange={(open) => !open && setSelectedRun(null)}>
        <SheetContent className="w-full sm:max-w-2xl bg-surface-card border-border-default overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-text-primary">Run Details</SheetTitle>
          </SheetHeader>

          {runDetailLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-accent-primary border-t-transparent rounded-full" />
            </div>
          ) : runDetail ? (
            <Tabs defaultValue="output" className="mt-6">
              <TabsList className="bg-bg-tertiary w-full grid grid-cols-4">
                <TabsTrigger value="output">Story Output</TabsTrigger>
                <TabsTrigger value="grok">Grok Log</TabsTrigger>
                <TabsTrigger value="di">DI Output</TabsTrigger>
                <TabsTrigger value="logs">Logs</TabsTrigger>
              </TabsList>

              <TabsContent value="output" className="mt-4 space-y-4">
                <div className="flex gap-2">
                  {runDetail.status === 'review' && (
                    <>
                      <Button onClick={() => handleApproveRun(runDetail.id)} data-testid="approve-run-btn">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve & Submit
                      </Button>
                      <Button variant="outline" onClick={() => handleRejectRun(runDetail.id)}>
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </>
                  )}
                </div>

                <div className="bg-bg-tertiary p-4 rounded-lg">
                  <p className="text-xs text-text-tertiary uppercase mb-2">Headline</p>
                  <p className="text-lg font-medium text-text-primary">{runDetail.headline || '-'}</p>
                </div>

                <div className="bg-bg-tertiary p-4 rounded-lg">
                  <p className="text-xs text-text-tertiary uppercase mb-2">Lede</p>
                  <p className="text-text-secondary">{runDetail.lede || '-'}</p>
                </div>

                <div className="bg-bg-tertiary p-4 rounded-lg">
                  <p className="text-xs text-text-tertiary uppercase mb-2">Body</p>
                  <div 
                    className="prose prose-invert prose-sm max-w-none text-text-secondary"
                    dangerouslySetInnerHTML={{ __html: runDetail.body || '<p>No content</p>' }}
                  />
                </div>
              </TabsContent>

              <TabsContent value="grok" className="mt-4 space-y-4">
                <div className="bg-bg-tertiary p-4 rounded-lg">
                  <p className="text-xs text-text-tertiary uppercase mb-2">Prompt Sent</p>
                  <pre className="text-sm text-text-secondary whitespace-pre-wrap font-mono">
                    {runDetail.grok_prompt_sent || 'No prompt recorded'}
                  </pre>
                </div>

                <div className="bg-bg-tertiary p-4 rounded-lg">
                  <p className="text-xs text-text-tertiary uppercase mb-2">Raw Response</p>
                  <pre className="text-sm text-text-secondary whitespace-pre-wrap font-mono overflow-x-auto">
                    {runDetail.grok_response_raw || 'No response recorded'}
                  </pre>
                </div>
              </TabsContent>

              <TabsContent value="di" className="mt-4 space-y-4">
                <div className="bg-accent-primary/10 p-4 rounded-lg">
                  <p className="text-lg font-heading font-bold text-accent-primary">
                    {runDetail.di_records_created || 0} DI Records Created
                  </p>
                </div>

                {runDetail.di_records?.length > 0 ? (
                  <div className="space-y-3">
                    {runDetail.di_records.map((record) => (
                      <div key={record.id} className="bg-bg-tertiary p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="capitalize">{record.record_type}</Badge>
                        </div>
                        <p className="font-medium text-text-primary">{record.title}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-text-tertiary text-center py-8">No DI records generated</p>
                )}
              </TabsContent>

              <TabsContent value="logs" className="mt-4">
                <div className="bg-bg-tertiary p-4 rounded-lg space-y-2">
                  <p className="text-sm">
                    <span className="text-text-tertiary">Started:</span>{' '}
                    <span className="text-text-secondary">{formatDateTime(runDetail.started_at)}</span>
                  </p>
                  <p className="text-sm">
                    <span className="text-text-tertiary">Completed:</span>{' '}
                    <span className="text-text-secondary">{formatDateTime(runDetail.completed_at)}</span>
                  </p>
                  <p className="text-sm">
                    <span className="text-text-tertiary">Status:</span>{' '}
                    <StatusBadge status={runDetail.status} />
                  </p>
                  {runDetail.error_message && (
                    <p className="text-sm">
                      <span className="text-text-tertiary">Error:</span>{' '}
                      <span className="text-red-400">{runDetail.error_message}</span>
                    </p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default AutomatedStories;
